import React, { useEffect } from 'react';
import { steps } from './steps';
import styles from './styles.module.less';
import { Button } from '@galaxy-ds/core';

interface Props {}

export const Onboarding: React.FC<Props> = (props) => {
  const [visible, setVisible] = React.useState(false);
  const modalRef = React.useRef<HTMLDivElement>(null);
  const [modalStyles, setModalStyles] = React.useState<React.CSSProperties>({});
  const footerRef = React.useRef<HTMLDivElement>(null);
  const [index, setIndex] = React.useState(0);
  const [arrowPosition, setArrowPosition] = React.useState('none');

  const [arrowStyles, setArrowStyles] = React.useState<React.CSSProperties>({
    opacity: 0,
  });

  // on load we want to preload all the onboarding images
  useEffect(() => {
    steps.forEach((step) => {
      const img = new Image();
      img.src = step.image;
    });
  }, []);

  const step = steps[index];

  const onNext = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIndex(index + 1);
  };

  const onBack = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (index === 0) {
      disableOnboarding();
      return;
    } else {
      setIndex(index - 1);
    }
  };

  const positionOnboarding = () => {
    // Get the step.refId from the current step
    // and read the data-attribute from the DOM that matches the refId
    const refId = step?.refId;
    const ref = refId
      ? document.querySelector(`[data-onboarding-id='${refId}']`)
      : null;
    if (ref) {
      // Take the position of the ref and move the modalRef to that position
      const rect = ref.getBoundingClientRect();

      const modalRect = modalRef.current.getBoundingClientRect();
      const footerRect = footerRef.current.getBoundingClientRect();

      const position = ref.getAttribute('data-onboarding-position');
      switch (position) {
        case 'right': {
          setModalStyles({
            top:
              rect.top +
              rect.height / 2 -
              modalRect.height / 2 +
              0.5 * modalRect.height +
              footerRect.height / 2,
            left: rect.right + 20 + 0.5 * modalRect.width,
          });

          setArrowStyles({
            left: '-5px',
            top: 'calc(50% - 35px)',
            transform: 'translateX(-50%) translateY(-50%) rotate(-90deg)',
          });
          break;
        }
        case 'top': {
          const top = rect.top - modalRect.height - 20 + 0.5 * modalRect.height;
          let left = rect.left + rect.width / 2 - modalRect.width / 2;
          let arrowOffset = 0;

          // Check if modal overflows the page width
          const pageWidth = window.innerWidth;
          if (left + modalRect.width > pageWidth) {
            left = pageWidth - modalRect.width - 20 + 0.5 * modalRect.width;
            // setArrowPosition('bottom-right');
            arrowOffset = rect.left - left + rect.width / 2;
          } else {
            left = left + 0.5 * modalRect.width;
          }

          setArrowStyles({
            left: `calc(50% + ${arrowOffset}px)`,
            top: '100%',
            transform: 'rotate(180deg) translate(50%, 0)',
          });
          setModalStyles({
            top,
            left,
          });
          break;
        }
        case 'bottom': {
          const top = rect.bottom + 20 + 20 + 0.5 * modalRect.height;
          let left = rect.left + rect.width / 2 - modalRect.width / 2;
          let arrowOffset = 0;

          // Check if modal overflows the page width
          const pageWidth = window.innerWidth;
          if (left + modalRect.width > pageWidth) {
            left = pageWidth - modalRect.width - 20 + 0.5 * modalRect.width;
            // setArrowPosition('bottom-right');
            arrowOffset = rect.left - left + rect.width / 2;
          } else {
            left = left + 0.5 * modalRect.width;
          }

          setArrowStyles({
            left: `calc(50% + ${arrowOffset}px)`,
            top: '-5px',
            transform: 'translateX(-50%) translateY(-50%)',
          });
          setModalStyles({
            top,
            left,
          });
          break;
        }
        default: {
          // I want the modal to be centered 20px above the ref
          const top = rect.top - modalRect.height - 20 + 0.5 * modalRect.height;
          const left =
            rect.left +
            rect.width / 2 -
            modalRect.width / 2 +
            0.5 * modalRect.width;

          setModalStyles({
            top,
            left,
          });
          setArrowStyles({
            left: 'calc(50% - 5px)',
            top: 'calc(100% - 5px)',
            transform: 'translateX(-50%) translateY(-50%) rotate(0deg)',
          });
          break;
        }
      }
    } else {
      setModalStyles({
        top: '50%',
        left: '50%',
      });
      setArrowStyles({
        opacity: 0,
      });
    }
  };

  useEffect(() => {
    if (!step) {
      disableOnboarding();
      return;
    }
    positionOnboarding();
  }, [index, step?.refId]);

  // On escape press and on index 0, close the modal
  React.useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && index === 0) {
        disableOnboarding();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [index]);

  const disableOnboarding = () => {
    setVisible(false);
    // On visibility change to false, set a cookie to not show the modal again
    // for 365 days
    document.cookie = 'onboarding=false; max-age=31536000';
  };

  // On load, load onboarding cookie and set visibility
  React.useEffect(() => {
    const onboarding = document.cookie
      .split('; ')
      .find((row) => row.startsWith('onboarding='));

    if (onboarding) {
      disableOnboarding();
    } else {
      setVisible(true);
    }
  }, []);

  if (!step) {
    return null;
  }

  return (
    <div
      className={styles.onboardingModalWrapper}
      style={{
        opacity: visible ? 1 : 0,
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      <div className={styles.relativeWrapper}>
        <div
          ref={modalRef}
          className={styles.onboardingModal}
          style={modalStyles}
          data-onboarding-arrow-position={arrowPosition}
        >
          <div className={styles.body}>
            <img src={step?.image} alt='onboarding' />
            <div className={styles.title}>{step.title}</div>
            <div className={styles.description}>{step.description}</div>
          </div>
          <div ref={footerRef} className={styles.footer}>
            <Button
              variant='outlined'
              style={{ visibility: !step?.onBackLabel ? 'hidden' : undefined }}
              className={styles.backButton}
              onClick={onBack}
            >
              {step?.onBackLabel}
            </Button>
            <Button
              variant='contained'
              color='primary'
              className={styles.nextButton}
              onClick={onNext}
            >
              {step?.onNextLabel}
            </Button>
          </div>

          <div style={arrowStyles} className={styles.arrow} />
        </div>
      </div>
    </div>
  );
};
